import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";

import { showNotification } from "../../../helpers/ShowNotification";
import {
  refreshPrices,
  clearRefreshPricesResult,
} from "../../../redux/CarsManagement/Cars/carsSlice";

// Normalize the backend result into a consistent shape regardless of casing.
const normalizeResult = (result) => {
  if (!result) return null;

  const data = result.data ?? result;

  const updatedCount =
    data.updatedCount ?? data.UpdatedCount ?? data.updated ?? data.Updated ?? 0;
  const failedCount =
    data.failedCount ?? data.FailedCount ?? data.failed ?? data.Failed ?? 0;

  const rawErrors =
    data.errors ?? data.Errors ?? data.failedRows ?? data.FailedRows ?? [];

  const errors = (Array.isArray(rawErrors) ? rawErrors : []).map((item) => ({
    variationCode:
      item.variationCode ??
      item.VariationCode ??
      item.variationcode ??
      item.code ??
      item.Code,
    reason:
      item.reason ??
      item.Reason ??
      item.message ??
      item.Message ??
      item.error ??
      item.Error,
  }));

  return { updatedCount, failedCount, errors };
};

function RefreshPrices() {
  const dispatch = useDispatch();
  const { isLoading, refreshPricesResult } = useSelector((state) => state.cars);

  const [file, setFile] = useState(null);

  // Clear any previous result when entering the screen.
  useEffect(() => {
    dispatch(clearRefreshPricesResult());
  }, [dispatch]);

  const result = normalizeResult(refreshPricesResult);

  const handleFileChange = (selected) => {
    setFile(selected || null);
    dispatch(clearRefreshPricesResult());
  };

  const handleRefresh = () => {
    if (!file) {
      showNotification({
        message: "يرجى اختيار ملف الإكسيل أولاً",
        status: "warning",
      });
      return;
    }

    const formData = new FormData();
    formData.append("File", file);

    dispatch(refreshPrices(formData));
  };

  return (
    <>
      {/* Breadcrumb */}
      <nav className="uk-position-relative" aria-label="Breadcrumb">
        <ul className="uk-breadcrumb">
          <li>
            <NavLink to="/Cars">السيارات</NavLink>
          </li>
          <li>
            <span aria-current="page">تحديث الأسعار</span>
          </li>
        </ul>
      </nav>

      <div className="uk-padding-small uk-background-default uk-border-rounded">
        <p className="uk-text-muted">
          قم برفع ملف إكسيل يحتوي على عمودين: رمز الموديل (variationcode) والسعر
          (price) لتحديث الأسعار.
        </p>

        <div className="uk-grid-small" uk-grid="">
          {/* File Upload */}
          <div className="uk-width-1-1 uk-width-1-2@m">
            <label className="uk-form-label uk-text-bold">ملف الإكسيل</label>
            <div className="uk-margin-small-top">
              <input
                type="file"
                accept=".xlsx,.xls"
                className="uk-input"
                onChange={(e) => handleFileChange(e.target.files?.[0])}
              />
              {file && (
                <div className="uk-text-success uk-margin-small-top">
                  {file.name}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="uk-margin-top uk-text-left">
          <button
            type="button"
            disabled={isLoading}
            onClick={handleRefresh}
            className="uk-button uk-button-secondary uk-margin-small-left"
          >
            {isLoading ? <div uk-spinner=""></div> : "تحديث الأسعار"}
          </button>
          <NavLink to="/Cars" className="uk-button uk-button-primary">
            إلغاء
          </NavLink>
        </div>

        {/* Result Summary */}
        {result && (
          <div className="uk-margin-top">
            <h4 className="uk-heading-line">
              <span>نتيجة التحديث</span>
            </h4>

            <div className="uk-grid-small uk-child-width-1-2@s" uk-grid="">
              <div>
                <div className="uk-card uk-card-default uk-card-body uk-card-small uk-border-rounded">
                  <span className="uk-text-success uk-text-bold uk-text-large">
                    {result.updatedCount}
                  </span>
                  <div className="uk-text-muted">تم تحديثها بنجاح</div>
                </div>
              </div>
              <div>
                <div className="uk-card uk-card-default uk-card-body uk-card-small uk-border-rounded">
                  <span className="uk-text-danger uk-text-bold uk-text-large">
                    {result.failedCount || result.errors.length}
                  </span>
                  <div className="uk-text-muted">فشل تحديثها</div>
                </div>
              </div>
            </div>

            {/* Per-row errors */}
            {result.errors.length > 0 && (
              <div className="uk-margin-top uk-overflow-auto">
                <table className="uk-table uk-table-divider uk-table-small uk-table-striped">
                  <thead>
                    <tr>
                      <th>رمز الموديل</th>
                      <th>السبب</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((err, index) => (
                      <tr key={`${err.variationCode ?? "row"}-${index}`}>
                        <td>{err.variationCode ?? "-"}</td>
                        <td className="uk-text-danger">{err.reason ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default RefreshPrices;
